import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Edit,
  Save,
  X,
  Eye,
  EyeOff,
  Building2,
  ShieldCheck,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import config from '../../config';
import Gouvernerat from '../../utils/Gouvernerat.json';

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [address, setAddress] = useState({
    pays: 'Tunisie',
    Gouvernorat: '',
    adresse: ''
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset
  } = useForm({
    mode: 'onChange'
  });

  const password = watch('password');

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Non authentifié');

      const response = await axios.get(`${config.REACT_APP_API_URL}/user-profile`, {
        headers: { Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY }
      });

      const user = response.data;

      setValue('company_name', user.company_name || '');
      setValue('tax_registre', user.tax_registre || '');
      setValue('first_name', user.first_name || '');
      setValue('last_name', user.last_name || '');
      setValue('email', user.email || '');
      setValue('phone', user.phone || '');

      // Gestion de l'adresse
      if (user.adresse && typeof user.adresse === 'object') {
        setAddress({
          pays: user.adresse.pays || 'Tunisie',
          Gouvernorat: user.adresse.Gouvernorat || '',
          adresse: user.adresse.adresse || ''
        });
        setValue('adresse', user.adresse.adresse || '');
      } else {
        setAddress(prev => ({ ...prev, adresse: user.adresse || '' }));
        setValue('adresse', user.adresse || '');
      }
    } catch (error) {
      console.error(error.response?.data?.message || 'Impossible de charger le profil', {
        position: 'top-right'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const onSubmit = async (data) => {
    setSubmitting(true);

    try {
      const token = localStorage.getItem('accessToken');
      const payload = {
        company_name: data.company_name,
        tax_registre: data.tax_registre,
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        adresse: address
      };

      // Ajout du mot de passe uniquement s'il est rempli
      if (data.password && data.password.trim()) {
        if (data.password !== data.confirm_password) {
          console.error('Les mots de passe ne correspondent pas');
          return;
        }
        payload.password = data.password;
      }

      const response = await axios.put(
        `${config.REACT_APP_API_URL}/update_customer`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY } }
      );

      toast.success(response.data.message || 'Profil mis à jour avec succès', {
        position: 'top-right'
      });

      setIsEditing(false);
      reset(data); // met à jour les valeurs affichées
      await fetchProfile(); // recharge les données fraîches
    } catch (error) {
      console.error(error.response?.data?.message || 'Erreur lors de la mise à jour', {
        position: 'top-right'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    fetchProfile(); // recharge les données originales
  };

  const handleGouvernoratChange = (e) => {
    const value = e.target.value;
    setAddress(prev => ({ ...prev, Gouvernorat: value }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddress(prev => {
      const updated = { ...prev, [name]: value };
      setValue('adresse', updated.adresse); // synchronisation avec react-hook-form
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
          <p className="text-gray-600 dark:text-gray-400">Chargement du profil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-9xl mx-auto space-y-8 pb-12">
      <ToastContainer position="top-right" theme="colored" limit={3} />

      {/* En-tête */}
      <div className="flex flex-col min-sm:flex-row min-sm:items-center min-sm:justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="bg-emerald-100 dark:bg-emerald-900/40 p-4 rounded-full">
            <User className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="mb-0 text-lg min-sm:text-xl font-bold text-gray-900 dark:text-white tracking-tight">
              Mon profil
            </h1>
            <p className="hidden min-sm:visible text-gray-500 dark:text-gray-400 mt-1">
              Gérez vos informations personnelles et de société
            </p>
          </div>
        </div>

        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg shadow-md transition-colors"
          >
            <Edit size={18} />
            Modifier le profil
          </button>
        ) : (
          <div className="flex gap-3 justify-end">
            <button
              type="submit"
              form="profile-form"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg shadow-md disabled:opacity-50 transition-colors"
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Save size={18} />
              )}
              Enregistrer
            </button>

            <button
              onClick={handleCancel}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-lg disabled:opacity-50 transition-colors"
            >
              <X size={18} />
              Annuler
            </button>
          </div>
        )}
      </div>

      {/* Formulaire */}
      <form id="profile-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Bloc Société */}
        <div className="dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 min-lg:p-8">
          <h2 className="text-lg min-sm:text-xl font-semibold mb-6 flex items-center gap-3 text-gray-900 dark:text-white">
            <Building2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            Informations société
          </h2>

          <div className="grid grid-cols-1 min-md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Raison sociale
              </label>
              {isEditing ? (
                <input
                  {...register('company_name', { required: 'Champ obligatoire' })}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition ${
                    errors.company_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                  } dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                />
              ) : (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-gray-100 border border-transparent">
                  {watch('company_name') || '—'}
                </div>
              )}
              {errors.company_name && (
                <p className="mt-1.5 text-sm text-red-600">{errors.company_name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Matricule fiscal
              </label>
              {isEditing ? (
                <input
                  {...register('tax_registre')}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              ) : (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-gray-100 border border-transparent">
                  {watch('tax_registre') || '—'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bloc Informations personnelles */}
        <div className="dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 min-lg:p-8">
          <h2 className="text-lg min-sm:text-xl font-semibold mb-6 flex items-center gap-3 text-gray-900 dark:text-white">
            <ShieldCheck className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            Informations personnelles
          </h2>

          <div className="grid grid-cols-1 min-md:grid-cols-2 gap-6">
            {/* Prénom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Prénom
              </label>
              {isEditing ? (
                <input
                  {...register('first_name', { required: 'Champ obligatoire' })}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition ${
                    errors.first_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                  } dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                />
              ) : (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-gray-100 border border-transparent">
                  {watch('first_name') || '—'}
                </div>
              )}
              {errors.first_name && (
                <p className="mt-1.5 text-sm text-red-600">{errors.first_name.message}</p>
              )}
            </div>

            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nom
              </label>
              {isEditing ? (
                <input
                  {...register('last_name', { required: 'Champ obligatoire' })}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition ${
                    errors.last_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                  } dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                />
              ) : (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-gray-100 border border-transparent">
                  {watch('last_name') || '—'}
                </div>
              )}
              {errors.last_name && (
                <p className="mt-1.5 text-sm text-red-600">{errors.last_name.message}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Adresse email
              </label>
              {isEditing ? (
                <input
                  type="email"
                  {...register('email', {
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Adresse email invalide'
                    }
                  })}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition ${
                    errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                  } dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                />
              ) : (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-gray-100 border border-transparent flex items-center gap-2">
                  <Mail size={16} className="text-gray-500" />
                  {watch('email') || '—'}
                </div>
              )}
              {errors.email && (
                <p className="mt-1.5 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Téléphone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Téléphone
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  {...register('phone', {
                    pattern: {
                      value: /^((\+|00)216)?([2579][0-9]{7}|42[0-9]{6})$/,
                      message: 'Numéro de téléphone invalide'
                    }
                  })}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition ${
                    errors.phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                  } dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                  placeholder="+216 XX XXX XXX"
                />
              ) : (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-gray-100 border border-transparent flex items-center gap-2">
                  <Phone size={16} className="text-gray-500" />
                  {watch('phone') || '—'}
                </div>
              )}
              {errors.phone && (
                <p className="mt-1.5 text-sm text-red-600">{errors.phone.message}</p>
              )}
            </div>

            {/* Gouvernorat */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Gouvernorat
              </label>
              {isEditing ? (
                <select
                  value={address.Gouvernorat}
                  onChange={handleGouvernoratChange}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Sélectionner un gouvernorat</option>
                  {Gouvernerat.Gouvernorat.map((gouv) => (
                    <option key={gouv.Nom} value={gouv.Nom}>
                      {gouv.Nom}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-gray-100 border border-transparent flex items-center gap-2">
                  <MapPin size={16} className="text-gray-500" />
                  {address.Gouvernorat || '—'}
                </div>
              )}
            </div>

            {/* Adresse complète */}
            <div className="min-md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Adresse complète
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={address.adresse}
                  onChange={handleAddressChange}
                  name="adresse"
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Rue, numéro, immeuble, étage..."
                />
              ) : (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-gray-100 border border-transparent flex items-center gap-2">
                  <MapPin size={16} className="text-gray-500" />
                  {address.adresse || '—'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Changement de mot de passe (uniquement en mode édition) */}
        {isEditing && (
          <div className="dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 min-lg:p-8">
            <h2 className="text-lg min-sm:text-xl font-semibold mb-6 flex items-center gap-3 text-gray-900 dark:text-white">
              <ShieldCheck className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              Changer le mot de passe
            </h2>

            <div className="grid grid-cols-1 min-md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {
                      minLength: {
                        value: 6,
                        message: 'Minimum 6 caractères'
                      }
                    })}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition pr-12 ${
                      errors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                    } dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...register('confirm_password', {
                      validate: (val) =>
                        !password || val === password || 'Les mots de passe ne correspondent pas'
                    })}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition pr-12 ${
                      errors.confirm_password ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                    } dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.confirm_password && (
                  <p className="mt-1.5 text-sm text-red-600">{errors.confirm_password.message}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default Profile;